import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import styles from "./Tutorials.module.css";
import { PROGRAMS, computeSubjectStats } from "../config/tutorialCatalogue";
import tutorialHeroImg from './image/prrepx_tutorial0.png';
import tutorialJpg from './image/tutorial0.png';
import SocialSidebar from "../components/SocialSideBar";
import { useAllProgress } from "../utils/useProgress";
import { useEnrollment } from "../utils/useEnrollment";
import { useAuth } from "../context/AuthContext";
import EnrollmentFormModal from "../components/EnrollmentFormModal";
import EnrolledSuccessModal from "../components/EnrolledSuccessModal";

// --- SVG Icons ---
const BookOpenIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>
);
const AtomIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(-30 12 12)"/><ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(30 12 12)"/><ellipse cx="12" cy="12" rx="10" ry="4"/></svg>
);
const DnaIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14.5A4.5 4.5 0 0 1 8.5 10H10a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-1.5A4.5 4.5 0 0 0 4 18.5"/><path d="M20 9.5A4.5 4.5 0 0 0 15.5 5H14a2 2 0 0 0-2 2v0a2 2 0 0 0 2 2h1.5A4.5 4.5 0 0 1 20 13.5"/></svg>
);
const CalculatorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="16" height="20" x="4" y="2" rx="2"/><line x1="8" x2="16" y1="6" y2="6"/><line x1="16" x2="16" y1="14" y2="18"/><path d="M16 10h.01"/><path d="M12 10h.01"/><path d="M8 10h.01"/><path d="M12 14h.01"/><path d="M8 14h.01"/><path d="M12 18h.01"/><path d="M8 18h.01"/></svg>
);
const FlaskIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8.5 2h7l5.3 16H3.2L8.5 2z"/><path d="M7 16h10"/><path d="M10 9h4"/></svg>
);
const FeatherIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/><line x1="17.5" y1="15" x2="9" y2="15"/></svg>
);
const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
);
const GoogleIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" style={{ marginRight: 6, verticalAlign: 'middle', flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// --- Subject icon map ---
const SUBJECT_ICONS = {
  math: <CalculatorIcon />,
  physics: <AtomIcon />,
  chemistry: <FlaskIcon />,
  biology: <DnaIcon />,
  science: <BookOpenIcon />,
  english: <FeatherIcon />,
};

// --- Program-level progress from raw allProgress data ---
function getProgramProgress(allProgress, program) {
  let done = 0, total = 0;
  (program.subjects || []).forEach(s => {
    if (s.comingSoon) return;
    const stats = computeSubjectStats(s.subjectKey, allProgress);
    done  += stats.done;
    total += stats.total;
  });
  return { done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
}

const CATEGORIES = [
  { id: 'all',         label: 'All Programs' },
  { id: 'competitive', label: 'JEE / NEET' },
  { id: 'board',       label: 'Class 11-12' },
  { id: 'foundation',  label: 'Class 6-10' },
];

const CATEGORY_LABELS = {
  competitive: 'Competitive Track',
  board: 'Board Excellence',
  foundation: 'Foundation Track',
};



const ProgramCatalogThumbnail = ({ program }) => {
  const allSubjects = program.subjects || [];
  const orderedSubjects = ['physics', 'chemistry', 'math', 'science', 'biology', 'english']
    .map(icon => allSubjects.find(subject => subject.icon === icon))
    .filter(Boolean);

  const showFourSubjects = ['class11', 'class12'].includes(program.id);
  const featuredSubjects = showFourSubjects
    ? orderedSubjects.slice(0, 4)
    : orderedSubjects.slice(0, 3);

  return (
    <div className={styles.catalogCardThumbnail}>
      <div className={styles.thumbnailBackdrop}>
        <div className={styles.thumbnailGrid} />
        <div className={styles.thumbnailGlow} />
        <div className={styles.thumbnailTopRow}>
          <span className={styles.thumbnailBrand}>Lectures・DPP・Tests</span>
          
        </div>

        <div className={styles.thumbnailMainRow}>
          <div className={styles.thumbnailCopy}>
            <span className={styles.thumbnailKicker}>India's Most Structured Course</span>
            <strong className={styles.thumbnailTitle}>{program.label}</strong>
            <span className={styles.thumbnailSubtitle}>{program.tagline}</span>
          </div>

          <div className={styles.thumbnailSubjectStack} aria-hidden="true">
            {featuredSubjects.map(subject => (
              <span
                key={subject.subjectKey}
                className={`${styles.thumbnailSubjectBadge} ${styles[`thumbnailSubjectBadge_${subject.icon}`]}`}
              >
                {SUBJECT_ICONS[subject.icon]}
                {subject.title}
              </span>
            ))}
          </div>
        </div>

        <div className={styles.thumbnailFooterRow}>
          <span className={styles.thumbnailStatPill}>{program.stats}</span>
          <span className={styles.thumbnailFooterNote}>Curated lectures • NCERT aligned</span>
        </div>
      </div>
    </div>
  );
};

// --- Program catalog card ---
const ProgramCatalogCard = ({ program, isEnrolled, onEnroll, onViewCourses, onUnenroll, progStats, isLoggedIn, signingIn }) => (
  <div className={`${styles.catalogCard} ${isEnrolled ? styles.catalogCardEnrolled : ''}`} style={{ '--card-accent': `linear-gradient(135deg, ${program.gradFrom}, ${program.gradTo})`, '--accent-from': program.gradFrom }}>
    <div className={styles.catalogCardCover}>
      <div className={styles.catalogCardCoverLeft}>
        <h3 className={styles.catalogCardLabel}>{program.label}</h3>
        <span className={styles.catalogCardTagline}>{program.tagline}</span>
      </div>
      {isEnrolled
        ? <span className={styles.enrolledBadge}>Enrolled</span>
        : <span className={styles.freeBadge}>FREE</span>
      }
    </div>
    {isEnrolled && progStats && (
      <div className={styles.enrolledCardProgressWrap}>
        <div className={styles.enrolledCardProgressBar}>
          <div
            className={styles.enrolledCardProgressFill}
            style={{ width: `${progStats.pct}%`, background: `linear-gradient(90deg, ${program.gradFrom}, ${program.gradTo})` }}
          />
        </div>
        <span className={styles.enrolledCardProgressText}>
          {`Progress: ${progStats.pct}% · ${progStats.done}/${progStats.total} items`}
        </span>
      </div>
    )}
    <ProgramCatalogThumbnail program={program} />
    <div className={styles.catalogCardFooter}>
      <span className={styles.catalogCardStats}>{program.stats} &bull; 100% Free</span>
      {isEnrolled ? (
        <div className={styles.enrolledCardActions}>
          <button className={styles.goBtn} onClick={() => onViewCourses(program)}>
            View Courses
          </button>
          <button
            className={styles.unenrollBtnSmall}
            onClick={() => onUnenroll(program.id, program.label)}
            title="Leave program"
          >
            &#x2715;
          </button>
        </div>
      ) : (
        <button
          className={styles.enrollBtn}
          onClick={() => onEnroll(program.id)}
        >
          Enroll Now (₹0 Free) &rarr;
        </button>
      )}
    </div>
  </div>
);

// --- Login-required modal (shown to guests who click Enroll) ---
const LoginEnrollModal = ({ program, onClose, onSignIn, signingIn }) => {
  if (!program) return null;
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.loginEnrollModal} onClick={e => e.stopPropagation()}>
        {/* Coloured header matching the program */}
        <div
          className={styles.loginEnrollModalHeader}
          style={{ background: `linear-gradient(135deg, ${program.gradFrom}, ${program.gradTo})` }}
        >
          <span className={styles.loginEnrollEmoji}>{program.emoji}</span>
          <button className={styles.modalClose} onClick={onClose}>&#x2715;</button>
        </div>

        <div className={styles.loginEnrollModalBody}>
          <div className={styles.loginEnrollLockIcon}>
            <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <h3 className={styles.loginEnrollTitle}>Sign in to Enroll</h3>
          <p className={styles.loginEnrollDesc}>
            Create a free account to enroll in <strong>{program.label}</strong>, track your progress,
            and access all courses across every program &mdash; 100% free.
          </p>

          <button
            className={styles.googleSignInBtn}
            onClick={onSignIn}
            disabled={signingIn}
          >
            {signingIn ? (
              <span className={styles.googleSignInBtnSpinner} />
            ) : (
              <GoogleIcon />
            )}
            {signingIn ? 'Signing in…' : 'Continue with Google'}
          </button>

          <p className={styles.loginEnrollNote}>
            By signing in you agree to our Terms of Service. No credit card required.
          </p>
        </div>
      </div>
    </div>
  );
};

// --- Main component ---
const Tutorials = () => {
  const navigate = useNavigate();
  const { enroll, unenroll, isEnrolled, enrolled, enrollmentLoading } = useEnrollment();
  const allProgress = useAllProgress(enrolled);
  const { currentUser, userData, signInWithGoogle } = useAuth();
  const [activeCategory, setActiveCategory] = useState('all');
  const [loginModalProgram, setLoginModalProgram] = useState(null); // login modal
  const [enrollFormProgram, setEnrollFormProgram] = useState(null); // details form modal
  const [successProgram, setSuccessProgram] = useState(null);   // post-enroll celebration
  const [signingIn, setSigningIn] = useState(false);
  const pendingEnrollId = useRef(null);
  const migratedRef = useRef(false);

  // Migration: users enrolled before the details form must re-enroll via the form
  // so badges / certificates / progress tracking all work from fresh data.
  // Gentle: clears the enrolled list (forces re-enroll via form) but keeps progress.
  useEffect(() => {
    if (!currentUser || !userData || migratedRef.current) return;
    if ((userData.enrolledPrograms?.length || 0) > 0 && !userData.enrollmentDetails) {
      migratedRef.current = true;
      (async () => {
        try {
          await setDoc(doc(db, 'users', currentUser.uid), { enrolledPrograms: [] }, { merge: true });
          try { localStorage.removeItem(`ppx_ep_${currentUser.uid}`); } catch { /* ignore */ }
          toast.info('Please re-enroll via the quick form to unlock progress tracking, badges & certificates.', { autoClose: 6000 });
        } catch { /* offline — will retry next load */ }
      })();
    }
  }, [currentUser, userData]); // eslint-disable-line

  const myPrograms = PROGRAMS.filter(p => isEnrolled(p.id));
  const catalogPrograms = PROGRAMS.filter(p =>
    !isEnrolled(p.id) && (activeCategory === 'all' ? true : p.category === activeCategory)
  );

  // After Google sign-in completes, process pending enrollment → open details form
  useEffect(() => {
    if (currentUser && pendingEnrollId.current) {
      const programId = pendingEnrollId.current;
      pendingEnrollId.current = null;
      setEnrollFormProgram(PROGRAMS.find(p => p.id === programId) || null);
    }
  }, [currentUser]); // eslint-disable-line

  const finalizeEnroll = async (programId) => {
    const prog = PROGRAMS.find(p => p.id === programId);
    const ok = await enroll(programId);
    if (!ok) { toast.error('Could not save enrollment. Please check your connection and try again.'); return; }
    setSuccessProgram(prog || null); // celebrate!
    setTimeout(() => {
      document.getElementById('my-programs')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleEnroll = (programId) => {
    if (!currentUser) {
      // Show the login modal with the program context
      const prog = PROGRAMS.find(p => p.id === programId);
      setLoginModalProgram(prog);
      pendingEnrollId.current = programId;
      return;
    }
    // Logged-in: collect details first, then enroll
    setEnrollFormProgram(PROGRAMS.find(p => p.id === programId) || null);
  };

  const handleLoginModalSignIn = async () => {
    setSigningIn(true);
    try {
      await signInWithGoogle();
      setLoginModalProgram(null);
      // enrollment handled by useEffect once currentUser updates
    } catch (err) {
      pendingEnrollId.current = null;
      const cancelled = err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request';
      if (!cancelled) toast.error('Sign-in failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleUnenroll = (programId, label) => {
    const toastId = toast(
      <div style={{ fontFamily: 'Inter, sans-serif' }}>
        <p style={{ margin: '0 0 10px', fontWeight: 600, fontSize: '0.95rem' }}>
          Leave <strong>{label}</strong>?
        </p>
        <p style={{ margin: '0 0 14px', fontSize: '0.82rem', color: '#64748b' }}>
          Your progress will be cleared and you&apos;ll be removed from My Programs.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={async () => {
              toast.dismiss(toastId);
              const ok = await unenroll(programId);
              if (ok) toast.info(`Left ${label}`, { autoClose: 2000 });
              else toast.error('Could not leave the program. Please check your connection and try again.');
            }}
            style={{
              flex: 1, padding: '7px 0', background: '#ef4444', color: '#fff',
              border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem'
            }}
          >
            Yes, leave
          </button>
          <button
            onClick={() => toast.dismiss(toastId)}
            style={{
              flex: 1, padding: '7px 0', background: '#f1f5f9', color: '#374151',
              border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem'
            }}
          >
            Cancel
          </button>
        </div>
      </div>,
      { autoClose: false, closeOnClick: false, closeButton: false, style: { padding: '16px 18px', minWidth: 280 } }
    );
  };

  return (
    <div className={styles.tutorialsPageContainer}>
      <SocialSidebar />

      <LoginEnrollModal
        program={loginModalProgram}
        onClose={() => { setLoginModalProgram(null); pendingEnrollId.current = null; }}
        onSignIn={handleLoginModalSignIn}
        signingIn={signingIn}
      />

      {enrollFormProgram && (
        <EnrollmentFormModal
          programId={enrollFormProgram.id}
          programLabel={enrollFormProgram.label}
          onClose={() => setEnrollFormProgram(null)}
          onDone={() => {
            finalizeEnroll(enrollFormProgram.id);
            setEnrollFormProgram(null);
          }}
        />
      )}

      <EnrolledSuccessModal
        programLabel={successProgram?.label}
        studentName={userData?.enrollmentDetails?.latest?.fullName || userData?.enrollmentDetails?.fullName || userData?.username || currentUser?.displayName || ''}
        onClose={() => setSuccessProgram(null)}
      />

      {/* --- Hero --- */}
      <div className={styles.heroSection} style={{ backgroundImage: `url(${tutorialJpg})` }}>
        <div className={`${styles.heroGlow} ${styles.glow1}`} />
        <div className={`${styles.heroGlow} ${styles.glow2}`} />
        <div className={`${styles.heroGlow} ${styles.glow3}`} />

        {/* Left image — desktop only */}
        <div className={styles.heroImgLeft}>
          <div className={styles.heroImgFrameLeft}>
            <img src={tutorialHeroImg} alt="PrrepX Tutorials" className={styles.heroMainImg} />
            <div className={styles.heroImgOverlay} />
          </div>
        </div>

        {/* Mobile image — shown on mobile, hidden on desktop/tablet */}
        <div className={styles.heroMobileImg}>
          <img src={tutorialHeroImg} alt="PrrepX Tutorials" className={styles.heroMobileSlideImg} />
          <div className={styles.heroMobileImgBadge}>🎓 Most Premium Contents - Zero Barriers</div>
        </div>

        {/* Center — text content */}
        <div className={styles.heroContent}>
          <div className={styles.heroBadgePill}>🎓 Most Premium Contents - Zero Barriers</div>
          <h1 className={styles.heroHeading}>
            <span className={styles.heroBlue}>JEE/NEET & Class 6–12th</span>
            <br />
            <span className={styles.heroAccent}>Full Syllabus Video Lectures, DPPs, and Tests</span>
          </h1>
          <p className={styles.heroTagline}>
            At par with any paid coaching. PrrepX is providing 100% Free Access by top IIT/NIT & medical alumni.
          </p>

          {/* Crisp benefit strip — visible to everyone, drives enrollment 
          <div className={styles.heroBenefits}>
            <span className={styles.heroBenefit}><strong>100% Free</strong> · ₹0 forever</span>
            <span className={styles.heroBenefitSep} />
            <span className={styles.heroBenefit}>📈 <strong>Progress tracked</strong> on every lecture, DPP & test</span>
            <span className={styles.heroBenefitSep} />
            <span className={styles.heroBenefit}>🏅 <strong>Earn badges</strong> per chapter</span>
            <span className={styles.heroBenefitSep} />
            <span className={styles.heroBenefit}>📜 <strong>Certificates</strong> per subject</span>
            <span className={styles.heroBenefitSep} />
            <span className={styles.heroBenefit}>🎓 <strong>100% IIT/NIT & medical alumni</strong> faculty</span>
          </div>
           */}
           
          <div className={styles.heroButtons}>
            <a href="#catalog" className={styles.btnPrimary}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2.5">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              Browse Programs
            </a>
            <Link to="/doubt-portal24*7" className={styles.btnOutline}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              24×7 Doubt Portal
            </Link>
          </div>

          {!enrollmentLoading && myPrograms.length > 0 ? (
            <div className={styles.heroEnrolledBadge}>
              <CheckCircleIcon />
              &nbsp;Enrolled in <strong>{myPrograms.length}</strong> program{myPrograms.length > 1 ? 's' : ''}
            </div>
          ) : (
            <p className={styles.alumniText}>
              {currentUser
                ? '✨ Pick a program below and enroll — it\'s free, forever.'
                : '✨ Sign in to enroll & track your progress — 100% free.'
              }
            </p>
          )}
        </div>

        {/* Right image — desktop only */}
        <div className={styles.heroImageCol}>
          <div className={styles.heroImgFrame}>
            <img src={tutorialHeroImg} alt="PrrepX Tutorials" className={styles.heroMainImg} />
            <div className={styles.heroImgOverlay} />
          </div>
        </div>

        <div className={styles.heroScrollHint}>
          <span>Scroll to explore</span>
          <div className={styles.scrollArrow} />
        </div>
      </div>

      {/* ── TopBar ── */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.topBarLeft}>
            <span className={styles.topBarIcon}>🎓</span>
            <span className={styles.topBarTitle}>Tutorials</span>
          </div>
          <div className={styles.topBarTabs}>
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`${styles.topBarTabBtn} ${activeCategory === cat.id ? styles.topBarTabBtnActive : ''}`}
                onClick={() => { setActiveCategory(cat.id); document.getElementById('catalog')?.scrollIntoView({ behavior: 'smooth' }); }}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.pageBody}>

        {/* --- My Programs --- */}
        {!enrollmentLoading && myPrograms.length > 0 && (
          <section className={styles.myProgramsSection} id="my-programs">
            <div className={styles.sectionTitleRow}>
              <h2 className={styles.sectionTitle}>My Enrolled Programs</h2>
              <span className={styles.sectionSubtitle}>{myPrograms.length} enrolled</span>
            </div>
            <div className={styles.catalogGrid}>
              {myPrograms.map(program => (
                <ProgramCatalogCard
                  key={program.id}
                  program={program}
                  isEnrolled={true}
                  onEnroll={handleEnroll}
                  onViewCourses={program => navigate(`/tutorials/course/${program.id}`)}
                  onUnenroll={handleUnenroll}
                  progStats={getProgramProgress(allProgress, program)}
                />
              ))}
            </div>
          </section>
        )}

        {/* --- Browse Catalog --- */}
        <section className={styles.catalogSection} id="catalog">
          <div className={styles.sectionTitleRow}>
            <h2 className={styles.sectionTitle}>
              {myPrograms.length > 0 ? 'Browse All Programs' : 'Choose Your Program'}
            </h2>
            <span className={styles.sectionSubtitle}>All 100% Free</span>
          </div>

          <div className={styles.catalogGrid}>
            {catalogPrograms.length === 0 ? (
              <p className={styles.allEnrolledMsg}>You&apos;re enrolled in all programs in this category!</p>
            ) : (
              catalogPrograms.map(program => (
                <ProgramCatalogCard
                  key={program.id}
                  program={program}
                  isEnrolled={false}
                  onEnroll={handleEnroll}
                  onViewCourses={program => navigate(`/tutorials/course/${program.id}`)}
                  onUnenroll={handleUnenroll}
                />
              ))
            )}
          </div>
        </section>

      </div>

      {/* ── CTA BANNER ── */}
      <section className={styles.ctaBanner}>
        <div className={styles.ctaBannerInner}>
          <h2 className={styles.ctaBannerTitle}>Ready to Start Practice?</h2>
          <p className={styles.ctaBannerSub}>PrrepX Test - One Subscription, Complete Access Model: 471+ Premium Scheduled Tests, Unlimited Open Zone Tests &amp; 80+ Free PYQ Tests.</p>
          <div className={styles.ctaBannerBtns}>
            <a href="https://testseries.prrepxpathshala.live/test-series/pyq" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtn}>🎯 Free PYQ Tests</a>
            <a href="https://testseries.prrepxpathshala.live/test-series/jee-premium" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtnSecondary}>⚡ JEE Premium Tests</a>
            <a href="https://testseries.prrepxpathshala.live/test-series/neet-premium" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtnSecondary}>🩺 NEET Premium Tests</a>
          </div>
        </div>
      </section>

      {/* ── FOOTER NOTE ── */}
      <div className={styles.footerNote}>
        <p>© {new Date().getFullYear()}-{String(new Date().getFullYear() + 1).slice(-2)} PrrepX Pathshala · Led by 100% IIT/NIT & top medical Alumni.</p>
      </div>

    </div>

  );
};

export default Tutorials;
